import React, { useEffect, useState } from "react";
import { Download, CloudOff, CheckCircle2, Loader2, Trash2, Package, WifiOff, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import { saveGuide, removeGuide, isSaved, offlineSupported } from "../lib/offline";

const LOW_SIGNAL = new Set(["spotty", "none"]);
const tripPackUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/adventures/${id}/trip-pack.zip`;

/**
 * Explicit "Save for offline" toggle for an adventure. Pre-caches the guide's
 * detail, photos and GPX so it opens with no signal at the trailhead. When the
 * adventure reports limited cell service it proactively nudges the user to
 * save before they go. Once saved, offers a one-tap "trip pack" download
 * (GPX + key photos + trail brief as a single shareable .zip).
 */
export default function SaveOfflineButton({ adv }) {
    const [saved, setSaved] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pct, setPct] = useState(0);
    const [promptDismissed, setPromptDismissed] = useState(false);

    useEffect(() => {
        setSaved(isSaved(adv.id));
        try {
            setPromptDismissed(!!localStorage.getItem(`rwo_offline_prompt_${adv.id}`));
        } catch { /* ignore */ }
    }, [adv.id]);

    if (!offlineSupported()) return null;

    const lowSignal = LOW_SIGNAL.has(String(adv.cell_service || "").toLowerCase());
    const showPrompt = lowSignal && !saved && !promptDismissed;

    const onSave = async () => {
        setBusy(true);
        setPct(0);
        try {
            await saveGuide(adv, (done, total) => setPct(Math.round((done / total) * 100)));
            setSaved(true);
            toast.success("Saved for offline — opens with no signal");
        } catch (e) {
            toast.error(e?.message || "Couldn't save offline");
        } finally {
            setBusy(false);
        }
    };

    const onRemove = async () => {
        setBusy(true);
        try {
            await removeGuide(adv.id);
            setSaved(false);
            toast.success("Removed from offline guides");
        } finally {
            setBusy(false);
        }
    };

    const dismissPrompt = () => {
        setPromptDismissed(true);
        try { localStorage.setItem(`rwo_offline_prompt_${adv.id}`, "1"); } catch { /* ignore */ }
    };

    const sharePack = async () => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/pack/${adv.id}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: adv.title || "Trip pack", text: "Grab the trip pack (route + photos)", url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Share link copied");
            }
        } catch { /* user cancelled share */ }
    };

    return (
        <div className="mb-6 space-y-3">
            {showPrompt && (
                <div
                    data-testid="offline-low-signal-prompt"
                    className="flex items-start gap-3 rounded-xl border border-ember-orange/30 bg-ember-orange/10 p-3"
                >
                    <span className="w-8 h-8 rounded-lg bg-ember-orange/15 text-ember-orange flex items-center justify-center shrink-0">
                        <WifiOff className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-midnight-pine leading-tight">
                            Limited cell service reported here
                        </p>
                        <p className="text-xs text-slate-gray">
                            Save this guide now so the route + photos open with no signal at the trailhead.
                        </p>
                        <button
                            data-testid="offline-low-signal-save"
                            onClick={onSave}
                            disabled={busy}
                            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ember-orange text-white text-xs font-bold hover:bg-ember-dark transition-all disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                            {busy ? `${pct}%` : "Save offline now"}
                        </button>
                    </div>
                    <button
                        data-testid="offline-low-signal-dismiss"
                        onClick={dismissPrompt}
                        className="text-slate-gray hover:text-granite shrink-0"
                        aria-label="Dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            <div
                data-testid="save-offline"
                className="flex items-center gap-3 rounded-xl border border-ridge-line bg-snowcap p-3"
            >
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${saved ? "bg-moss/15 text-moss" : "bg-alpine-mist text-slate-gray"}`}>
                    {saved ? <CheckCircle2 className="w-5 h-5" /> : <CloudOff className="w-5 h-5" />}
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-midnight-pine leading-tight">
                        {saved ? "Saved for offline" : "Save this guide for offline"}
                    </p>
                    <p className="text-xs text-slate-gray">
                        {busy
                            ? `Caching photos & route… ${pct}%`
                            : saved
                                ? "Photos + route are on your phone for the trailhead."
                                : "Pre-load photos + GPX before you lose signal."}
                    </p>
                </div>
                {saved ? (
                    <div className="flex items-center gap-2 shrink-0">
                        <a
                            href={tripPackUrl(adv.id)}
                            data-testid="trip-pack-download"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-midnight-pine text-white text-sm font-bold hover:bg-deep-evergreen transition-all"
                        >
                            <Package className="w-4 h-4" />
                            <span className="hidden sm:inline">Trip pack</span>
                        </a>
                        <button
                            data-testid="trip-pack-share"
                            onClick={sharePack}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-ridge-line text-granite hover:border-ember-orange hover:text-ember-orange transition-all"
                            aria-label="Share trip pack link"
                            title="Share trip pack link"
                        >
                            <Link2 className="w-4 h-4" />
                        </button>
                        <button
                            data-testid="save-offline-remove"
                            onClick={onRemove}
                            disabled={busy}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-ridge-line text-granite hover:border-trail-red hover:text-trail-red transition-all disabled:opacity-50"
                            aria-label="Remove from offline guides"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                    </div>
                ) : (
                    <button
                        data-testid="save-offline-btn"
                        onClick={onSave}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-midnight-pine text-white text-sm font-bold hover:bg-deep-evergreen transition-all disabled:opacity-60 shrink-0"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {busy ? `${pct}%` : "Save offline"}
                    </button>
                )}
            </div>
        </div>
    );
}
